import type { CSSProperties, ReactNode } from 'react';
import type { Unlock } from './pricingCopy';
import './pricing.css';

/*
 * Las piezas visuales de la página de precios y del bloque que xotski.com
 * enmarca: colores y tipografías de la maqueta, el botón sin estilos de
 * <button>, la viñeta con ✓, el chip de «próximamente» y la fila del sello.
 * Viven aparte para que `PricingPage` y `PricingEmbedPage` pinten lo mismo.
 */

export const C = {
  ink: '#1B0F2B', text: '#333333', mid: '#555555', muted: '#777777', dim: '#888888', faint: '#BBBBBB',
  line: '#E5E5E5', hair: '#F0F0F0', soft: '#F8F8F8', lilac: '#E2DFF2', lilacLine: '#E5E0F0',
  accent: '#B262F5', deep: '#6C1CD3', lime: '#C6D93B', white: '#FFFFFF', err: '#D14343',
} as const;
export const MONO = "'Space Mono', monospace";
export const SANS = "'DM Sans', sans-serif";
export const EASE = 'cubic-bezier(0.4,0,0.2,1)';

export type Mode = 'plans' | 'calc';

export const mono = (extra: CSSProperties = {}): CSSProperties => ({
  fontFamily: MONO, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: C.ink, ...extra,
});

/** Un `div[role=button]` como los de la maqueta: hover por clase, sin estilos de <button>. */
export function Btn({
  onClick, style, hover, children, ariaLabel,
}: { onClick?: () => void; style: CSSProperties; hover?: string; children: ReactNode; ariaLabel?: string }) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={hover}
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
    >
      {children}
    </div>
  );
}

export const Check = ({ children, size = 13, soon }: { children: ReactNode; size?: number; soon?: string }) => (
  <div style={{ display: 'flex', gap: 9, alignItems: 'baseline', fontSize: size, lineHeight: `${size + 7}px`, color: soon ? '#999999' : C.mid }}>
    <span style={{ color: soon ? '#CCCCCC' : C.accent, flex: 'none' }}>✓</span>
    <span>{children}</span>
    {soon ? (
      <span style={{ fontSize: 10, lineHeight: '14px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '1px 7px', borderRadius: 999, border: `1px solid ${C.line}`, color: '#999999', flex: 'none', alignSelf: 'center' }}>{soon}</span>
    ) : null}
  </div>
);

/** El chip de lo que aún no existe. */
export const SoonChip = ({ children, dark = false }: { children: ReactNode; dark?: boolean }) => (
  <span style={{ fontSize: 10, lineHeight: '14px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 999, border: `1px solid ${dark ? 'rgba(226,223,242,0.35)' : C.line}`, color: dark ? 'rgba(226,223,242,0.8)' : '#999999', flex: 'none' }}>{children}</span>
);

/** Una viñeta ya rellena: lo que aún no existe va apagado y con su chip. */
export const unlockRows = (list: Unlock[], withSoon: boolean, soonLabel: string) =>
  list
    .filter((b) => withSoon || typeof b === 'string')
    .map((b) => (typeof b === 'string' ? { text: b, soon: undefined } : { text: b.soon, soon: soonLabel }));

const SEAL_ICON = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={C.deep} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.5 2.5 2.5 4.5-5" />
  </svg>
);

/** «Powered by Onxite on every game.»: el sello, en cada tarjeta y en la respuesta. */
export const SealRow = ({ text, size = 12 }: { text: string; size?: number }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: size, lineHeight: `${size + 6}px`, color: C.dim }}>
    <span style={{ flex: 'none', display: 'inline-flex' }}>{SEAL_ICON}</span>
    <span>{text}</span>
  </div>
);

export const Section = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => (
  <section style={{ padding: '20px 28px 72px', display: 'flex', justifyContent: 'center', ...style }}>
    <div style={{ width: '100%', maxWidth: 1140 }}>{children}</div>
  </section>
);

export const H2 = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => (
  <h2 style={{ fontFamily: MONO, fontWeight: 700, fontSize: 'clamp(24px, 2.6vw, 32px)', lineHeight: 1.2, letterSpacing: '-0.02em', margin: 0, color: C.ink, ...style }}>
    {children}
  </h2>
);

export const Input = ({ style, ...rest }: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...rest}
    style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', borderRadius: 12, border: `1.5px solid ${C.line}`, background: C.white, color: C.ink, fontSize: 15, ...style }}
  />
);

